import React from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Egg, 
  Leaf, 
  Truck, 
  ShieldCheck, 
  Phone, 
  Mail, 
  MapPin, 
  AlertTriangle,
  Menu,
  ChevronRight
} from "lucide-react";

export default function Home() {
  return (
    <div className="min-h-screen font-sans flex flex-col w-full overflow-x-hidden">
      {/* Dev Banner */}
      <div className="bg-yellow-400 text-blue-900 px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2 z-50 relative">
        <AlertTriangle className="h-4 w-4" />
        <span>Сайт знаходиться в стадії розробки. Можливі тимчасові неполадки та неточності в роботі.</span>
      </div>

      {/* Navbar */}
      <header className="sticky top-0 w-full z-40 bg-white/80 backdrop-blur-md border-b border-blue-100 shadow-sm">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-2 rounded-lg text-yellow-400">
              <Egg className="h-6 w-6" />
            </div>
            <span className="font-bold text-xl tracking-tight text-blue-900">chickenplace</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Про компанію</a>
            <a href="#advantages" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Переваги</a>
            <a href="#products" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Продукція</a>
            <a href="#gallery" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Галерея</a>
            <a href="#contacts" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Контакти</a>
          </nav>
          <div className="md:hidden">
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-yellow-50/50" />
            <div className="absolute top-0 right-0 w-1/2 h-full opacity-5 transform translate-x-1/4">
              <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <path fill="#2563EB" d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,81.3,-46.3C90.8,-33.5,96.8,-18,97.2,-2.4C97.6,13.2,92.5,28.9,83.1,42.4C73.7,55.9,60,67.1,45.2,74.5C30.4,81.9,15.2,85.5,0.4,84.8C-14.4,84.1,-28.8,79.1,-41.8,71.3C-54.8,63.5,-66.4,52.9,-75.2,40.1C-84,27.3,-90,12.3,-91.1,-3.1C-92.2,-18.5,-88.4,-34.3,-79.3,-47.4C-70.2,-60.5,-55.8,-70.9,-41.1,-77.8C-26.4,-84.7,-13.2,-88.1,1.1,-89.8C15.4,-91.5,30.6,-83.6,44.7,-76.4Z" transform="translate(100 100)" />
              </svg>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
              <div className="w-full lg:w-1/2 space-y-8">
                <Badge className="bg-yellow-100 text-blue-800 hover:bg-yellow-200 border-none px-3 py-1 text-sm">
                  Натуральні продукти
                </Badge>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight">
                  Свіжі курячі яйця <br/>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-yellow-500">
                    прямо з ферми
                  </span>
                </h1>
                <p className="text-lg text-slate-600 max-w-xl">
                  Ми виробляємо якісні та екологічно чисті яйця. Дбаємо про здоров'я птахів, щоб на ваш стіл потрапляв лише найкращий продукт.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 shadow-lg shadow-blue-600/30 transition-transform hover:-translate-y-1" asChild>
                    <a href="#contacts">Замовити оптом</a>
                  </Button>
                  <Button size="lg" variant="outline" className="rounded-full px-8 border-blue-200 text-blue-700 hover:bg-blue-50 transition-transform hover:-translate-y-1" asChild>
                    <a href="#products">Наша продукція</a>
                  </Button>
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-tr from-blue-400 to-yellow-300 rounded-3xl transform rotate-3 scale-105 opacity-20 blur-xl"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1646900069241-f767d5d6dcf8?q=80&w=1200&auto=format&fit=crop" 
                    alt="Кошик свіжих яєць" 
                    className="relative rounded-3xl shadow-2xl object-cover w-full h-[400px] lg:h-[500px]"
                  />
                  <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-blue-100 flex items-center gap-4">
                    <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                      <ShieldCheck className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 font-medium">Гарантія якості</p>
                      <p className="font-bold text-slate-900">ДСТУ 5028:2008</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Про компанію Chickenplace</h2>
              <div className="w-24 h-1 bg-yellow-400 mx-auto rounded-full"></div>
              <p className="text-lg text-slate-600 leading-relaxed">
                Chickenplace — це сучасна птахофабрика, де традиції фермерства зустрічаються з передовими технологіями. Ми почали свій шлях з невеликої ферми і виросли в надійного постачальника якісних курячих яєць. Наша головна цінність — здоров'я споживачів, тому ми використовуємо лише натуральні корми та забезпечуємо птахам комфортні умови утримання.
              </p>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section id="advantages" className="py-24 bg-blue-50/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Наші переваги</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">Чому обирають продукцію Chickenplace</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: Leaf, title: "Екологічність", desc: "Тільки натуральні збалансовані корми без ГМО та стимуляторів росту." },
                { icon: Truck, title: "Свіжість", desc: "Оптимізована логістика: яйця потрапляють на прилавки в найкоротші терміни після збору." },
                { icon: ShieldCheck, title: "Контроль якості", desc: "Багаторівневий ветеринарний контроль на кожному етапі виробництва." },
                { icon: Egg, title: "Смак та користь", desc: "Яскравий жовток, міцна шкаралупа та максимум корисних мікроелементів." }
              ].map((adv, idx) => (
                <Card key={idx} className="border-none shadow-lg hover:shadow-xl transition-shadow bg-white/70 backdrop-blur-sm group">
                  <CardContent className="pt-8 px-6 pb-8 flex flex-col items-center text-center space-y-4">
                    <div className="p-4 bg-blue-100 rounded-2xl text-blue-600 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-yellow-400 transition-all duration-300">
                      <adv.icon className="h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">{adv.title}</h3>
                    <p className="text-slate-600">{adv.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section id="products" className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Наша продукція</h2>
                <p className="text-slate-600 max-w-xl">Широкий асортимент курячих яєць різних категорій для будь-яких потреб.</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: "Відбірна категорія (В)", weight: "від 73 г", desc: "Великі відбірні яйца для справжніх гурманів та випічки.", image: "https://images.unsplash.com/photo-1592762307574-e7543be803ec?q=80&w=800&auto=format&fit=crop" },
                { title: "Перша категорія (С1)", weight: "53-62.9 г", desc: "Оптимальний розмір на кожен день. Найпопулярніший вибір.", image: "https://images.pexels.com/photos/34851176/pexels-photo-34851176.jpeg?auto=compress&cs=tinysrgb&w=800" },
                { title: "Фермерське преміум", weight: "від 63 г", desc: "Яйця від курей вільного вигулу. Відрізняються особливо яскравим жовтком.", image: "https://images.unsplash.com/photo-1646900069241-f767d5d6dcf8?q=80&w=800&auto=format&fit=crop" }
              ].map((product, idx) => (
                <Card key={idx} className="overflow-hidden border border-slate-100 hover:border-blue-200 transition-colors group">
                  <div className="h-64 overflow-hidden relative">
                    <img src={product.image} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-yellow-400 text-blue-900 hover:bg-yellow-500 backdrop-blur-sm font-semibold shadow-sm">
                        {product.weight}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{product.title}</h3>
                    <p className="text-slate-600 mb-4">{product.desc}</p>
                    <Button variant="ghost" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-0 flex items-center gap-1 group/btn">
                      Детальніше <ChevronRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="py-24 bg-slate-900 text-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Галерея ферми</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">Погляньте на те, як влаштоване наше виробництво зсередини</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 row-span-2 overflow-hidden rounded-2xl">
                <img src="https://images.pexels.com/photos/12246335/pexels-photo-12246335.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Farm operations" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="overflow-hidden rounded-2xl h-48 md:h-auto">
                <img src="https://images.pexels.com/photos/35877058/pexels-photo-35877058.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Scale of production" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="overflow-hidden rounded-2xl h-48 md:h-auto">
                <img src="https://images.unsplash.com/photo-1534708906971-11115f37d6cb?q=80&w=800&auto=format&fit=crop" alt="Individual hen" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="col-span-2 overflow-hidden rounded-2xl h-48 md:h-64">
                <img src="https://images.pexels.com/photos/34851176/pexels-photo-34851176.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Trays of eggs" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </div>
          </div>
        </section>

        {/* Contacts & Requisites Section */}
        <section id="contacts" className="py-24 bg-white relative">
          <div className="absolute top-0 right-0 w-1/3 h-full bg-blue-50 rounded-l-full opacity-50 hidden lg:block pointer-events-none"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">Зв'яжіться з нами</h2>
                <p className="text-slate-600 mb-10">
                  Готові до співпраці? Залиште заявку, і наші менеджери зв'яжуться з вами для обговорення деталей оптових поставок.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-3 rounded-xl text-blue-600 shrink-0">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Адреса виробництва</h4>
                      <p className="text-slate-600 mt-1">08300, Київська обл., м. Бориспіль, вул. Фермерська, буд. 1</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-3 rounded-xl text-blue-600 shrink-0">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Телефон</h4>
                      <p className="text-slate-600 mt-1">+380 (44) 123-45-67</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-3 rounded-xl text-blue-600 shrink-0">
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Електронна пошта</h4>
                      <p className="text-slate-600 mt-1">info@chickenplace.com.ua</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-yellow-400"></div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6 mt-2">Реквізити компанії</h3>
                <div className="space-y-4 text-sm md:text-base">
                  <div className="flex justify-between border-b border-slate-200 pb-2">
                    <span className="text-slate-500">Повне найменування</span>
                    <span className="font-medium text-slate-900 text-right">ТОВ "ЧІКЕНПЛЕЙС"</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-2">
                    <span className="text-slate-500">Код ЄДРПОУ</span>
                    <span className="font-medium text-slate-900">32165498</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-2">
                    <span className="text-slate-500">ІПН</span>
                    <span className="font-medium text-slate-900">321654981234</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-2">
                    <span className="text-slate-500">Директор</span>
                    <span className="font-medium text-slate-900">Шевченко О. М.</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-2">
                    <span className="text-slate-500">IBAN</span>
                    <span className="font-medium text-slate-900 break-all ml-4 text-right">UA983052990000026001234567890</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-200 pb-2">
                    <span className="text-slate-500">Банк</span>
                    <span className="font-medium text-slate-900">АТ "ПРИВАТБАНК"</span>
                  </div>
                  <div className="flex justify-between pb-2">
                    <span className="text-slate-500">МФО</span>
                    <span className="font-medium text-slate-900">305299</span>
                  </div>
                </div>
                
                <div className="mt-8 p-4 bg-yellow-100/50 rounded-xl text-sm text-blue-900 flex gap-3 border border-yellow-200">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-600" />
                  <p>Увага! Дані реквізити згенеровані випадковим чином для демонстраційних цілей, оскільки сайт знаходиться в розробці.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-900">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Egg className="h-6 w-6 text-yellow-400" />
              <span className="font-bold text-xl tracking-tight text-white">chickenplace</span>
            </div>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Політика конфіденційності</a>
              <a href="#" className="hover:text-white transition-colors">Умови використання</a>
            </div>
          </div>
          <div className="mt-8 text-center text-sm border-t border-slate-800 pt-8">
            <p>&copy; {new Date().getFullYear()} Chickenplace. Всі права захищені.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
